import { takeEvery, put, all, select } from 'redux-saga/effects';
import { openModalDialog } from '@nextiva/crm-modals';

import { listReducer, showErrorNotification } from '@nextiva/engagement-ui';
import { castArray } from 'lodash';
import { SURVEY_STATUS_ARCHIVED, SURVEY_STATUS_DRAFT } from 'sharedParts';

import modalTypes from 'constants/AppModalTypes';
import * as actions from 'constants/surveyList/SurveyListActions';
import createBatchApiSurveyStatusPayload from 'utils/createBatchApiSurveyStatusPayload';
import getRejectedBatchErrorList from 'api/utils/getRejectedBatchErrorList';
import * as surveyApi from 'api/surveyList';

import {
  loadSurveyList,
  postCloneSurvey,
  deleteSurvey,
  batchSurveysStatus,
} from 'actions/surveyListActions';

import { navigateToSurveyEditor } from 'actions/templateListActions';
import { push } from 'connected-react-router';
import createQuickViewModalPath from '../../domains/quickView/utils/createQuickViewModalPath';

const { getItemListSelectedItemsIds } = listReducer;

function* onSurveyListActionEdit({ payload: listId }) {
  const selectedIds = yield select(getItemListSelectedItemsIds, listId);
  yield put(navigateToSurveyEditor(selectedIds[0]));
}

function* onSurveyListActionClone({ payload: listId }) {
  const selectedIds = yield select(getItemListSelectedItemsIds, listId);
  yield put(postCloneSurvey(listId, selectedIds[0]));
}

function* onSurveyListActionDelete({ payload: { listId, isShowTabsList} }) {
  const selectedIds = yield select(getItemListSelectedItemsIds, listId);
  const deleteQueue = castArray(selectedIds).map(id =>
    surveyApi.removeSurvey(id),
  );
  yield put(deleteSurvey(listId, () => Promise.all(deleteQueue), selectedIds, isShowTabsList));
}

function* onDeleteSurveySuccess({listId, isShowTabsList}) {
  yield put(loadSurveyList(listId, { pageNumber: 0 }, isShowTabsList));
}

function* changeSurveysStatus(listId, status, isShowTabsList) {
  const selectedIds = yield select(getItemListSelectedItemsIds, listId);
  yield put(
    batchSurveysStatus(
      listId,
      selectedIds,
      isShowTabsList,
      createBatchApiSurveyStatusPayload(selectedIds, { status }),
    ),
  );
}

function* onSurveyListActionArchive({ payload: { listId, isShowTabsList} }) {
  yield changeSurveysStatus(listId, SURVEY_STATUS_ARCHIVED, isShowTabsList);
}

function* onSurveyListActionUnarchive({ payload: {listId, isShowTabsList}}) {
  yield changeSurveysStatus(listId, SURVEY_STATUS_DRAFT, isShowTabsList);
}

function* onBatchSurveysStatusSuccess({ data }) {
  const errorCount = getRejectedBatchErrorList(data).length;
  if (errorCount) {
    const errorMessage =
      data.length > 1
        ? `${errorCount} of ${
            data.length
          } items were not processed due to server error`
        : "Couldn't complete action due to server error";
    put(showErrorNotification(errorMessage));
  }
}

function* onSurveyListOpenArchiveModal({ payload: { listId, isShowTabsList, archive } }) {
  const selectedIds = yield select(getItemListSelectedItemsIds, listId);
  yield put(
    openModalDialog({
      type: modalTypes.archiveSurvey,
      surveyIds: selectedIds,
      isShowTabsList,
      archive,
    }),
  );
}

function* onSurveyListOpenDeleteModal({ payload: {listId, typeConnect, templateIds} }) {
  const selectedIds = yield select(getItemListSelectedItemsIds, listId);
  yield put(
    openModalDialog({
      type: modalTypes.deleteSurvey,
      surveyIds: selectedIds,
      typeConnect,
      templateIds
    }),
  );
}

function* onNavigateToSurveyQuickView({ payload: listId }) {
  const selectedIdList = yield select(getItemListSelectedItemsIds, listId);
  yield put(push(createQuickViewModalPath(selectedIdList[0])));
}

export default function*() {
  yield all([
    takeEvery(
      actions.NAVIGATE_TO_SURVEY_QUICK_VIEW,
      onNavigateToSurveyQuickView,
    ),
    takeEvery(actions.SURVEY_LIST_ACTION_EDIT, onSurveyListActionEdit),
    takeEvery(actions.SURVEY_LIST_ACTION_CLONE, onSurveyListActionClone),
    takeEvery(actions.SURVEY_LIST_ACTION_DELETE, onSurveyListActionDelete),
    takeEvery(
      [
        actions.DELETE_SURVEY_SUCCESS,
        actions.BATCH_SURVEYS_STATUS_SUCCESS,
        actions.BATCH_SURVEYS_STATUS_FAILURE,
      ],
      onDeleteSurveySuccess,
    ),
    takeEvery(actions.SURVEY_LIST_ACTION_ARCHIVE, onSurveyListActionArchive),
    takeEvery(
      actions.SURVEY_LIST_ACTION_UNARCHIVE,
      onSurveyListActionUnarchive,
    ),
    takeEvery(
      actions.BATCH_SURVEYS_STATUS_SUCCESS,
      onBatchSurveysStatusSuccess,
    ),
    takeEvery(
      actions.SURVEY_LIST_OPEN_DELETE_MODAL,
      onSurveyListOpenDeleteModal,
    ),
    takeEvery(
      actions.SURVEY_LIST_OPEN_ARCHIVE_MODAL,
      onSurveyListOpenArchiveModal
    )
  ]);
}
